import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Mail, ArrowRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { FAQ_SECTIONS, renderFaqAnswer } from "@/data/faq";
import React from "react";

const LOGO_URL = "/logo-mbs.png";
const BASE_URL = "https://monbeausite.com";

export default function FAQPage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(FAQ_SECTIONS[0].id);

  // Sync with URL hash (#creation / #abonnement / #fin)
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash && FAQ_SECTIONS.some((s) => s.id === hash)) {
      setActiveSection(hash);
    }
    // Scroll to top on mount
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const handleSectionChange = (id) => {
    setActiveSection(id);
    window.history.replaceState(null, "", `/faq#${id}`);
  };

  const current = FAQ_SECTIONS.find((s) => s.id === activeSection);

  // Build JSON-LD FAQPage schema with ALL questions
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_SECTIONS.flatMap((s) =>
      s.items.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          // Strip markdown for SEO plain text
          text: item.a.replace(/\*\*/g, "").replace(/\n/g, " ").replace(/•\s*/g, ""),
        },
      }))
    ),
  };

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <Helmet>
        <title>FAQ — Toutes les réponses sur MonBeauSite | Création, abonnement et résiliation</title>
        <meta
          name="description"
          content="Toutes les réponses à vos questions sur la création de votre site, le fonctionnement de l'abonnement mensuel et les conditions de fin. Transparence totale sur les forfaits La Vitrine Active (39€/mois) et La Formule Croissance (59€/mois)."
        />
        <link rel="canonical" href={`${BASE_URL}/faq`} />
        <meta property="og:title" content="FAQ — MonBeauSite" />
        <meta property="og:description" content="Tout ce que vous voulez savoir avant de vous lancer : création, abonnement, résiliation." />
        <meta property="og:url" content={`${BASE_URL}/faq`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Minimal header */}
      <header className="border-b border-[#BFBFBF]/50 bg-white/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="faq-header-home-link">
            <img src={LOGO_URL} alt="MonBeauSite" className="h-7" />
          </Link>
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-xs nav-text tracking-wider uppercase text-[#4A4A4A] hover:text-[#C46A4A] transition-colors"
            data-testid="faq-back-home-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Retour à l'accueil
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute top-12 left-1/2 -translate-x-1/2 flex items-center gap-4 opacity-50" aria-hidden="true">
          <span className="h-px w-16 bg-gradient-to-r from-transparent to-[#D4AF37]/60" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/70" />
          <span className="h-px w-16 bg-gradient-to-l from-transparent to-[#D4AF37]/60" />
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="section-num font-mono-tech text-xs tracking-widest text-[#C46A4A] mb-5 inline-flex">FAQ // QUESTIONS FRÉQUENTES</p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-medium mb-6 text-[#1F3535] leading-[1.1] font-montserrat tracking-tight"
          >
            Toutes les réponses{" "}
            <span className="font-playfair italic text-[#C46A4A] font-normal">avant de vous lancer.</span>
          </h1>
          <p className="text-[#4A4A4A] text-base sm:text-lg font-body max-w-2xl mx-auto leading-relaxed">
            Transparence totale sur la création de votre site, le fonctionnement de l'abonnement et ce qui se passe à la fin. Si vous ne trouvez pas votre réponse ici, écrivez-moi — je réponds personnellement.
          </p>
        </div>
      </section>

      {/* Sticky section nav + content */}
      <div className="max-w-5xl mx-auto px-6 pb-24">
        {/* Sticky tabs */}
        <div className="sticky top-[57px] z-30 bg-[#FBF9F5] -mx-6 px-6 py-4 border-b border-[#BFBFBF]/30 mb-10">
          <div className="flex flex-wrap justify-center gap-2" role="tablist" data-testid="faq-page-tabs">
            {FAQ_SECTIONS.map((s) => {
              const isActive = s.id === activeSection;
              return (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => handleSectionChange(s.id)}
                  data-testid={`faq-page-tab-${s.id}`}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium font-body transition-all duration-300 border ${
                    isActive
                      ? "bg-[#2F4F4F] text-white border-[#2F4F4F] shadow-md shadow-[#2F4F4F]/10"
                      : "bg-white text-[#4A4A4A] border-[#BFBFBF]/60 hover:border-[#D4AF37]/50 hover:text-[#1F3535]"
                  }`}
                >
                  {s.label}
                  <span className="ml-2 text-[10px] opacity-60">{s.items.length}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Accordion */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
          >
            <Accordion
              type="single"
              collapsible
              defaultValue="item-0"
              className="space-y-3"
              data-testid={`faq-page-accordion-${activeSection}`}
            >
              {current.items.map((item, i) => (
                <AccordionItem
                  key={`${activeSection}-${i}`}
                  value={`item-${i}`}
                  className="bg-white border border-[#BFBFBF]/50 rounded-xl overflow-hidden data-[state=open]:border-[#D4AF37]/60 data-[state=open]:shadow-sm transition-all"
                >
                  <AccordionTrigger
                    className="px-6 py-5 text-left text-[#1F3535] font-medium text-base sm:text-[1.05rem] hover:no-underline hover:bg-[#FBF9F5]/50 transition-colors [&>svg]:text-[#C46A4A] font-montserrat tracking-tight"
                    data-testid={`faq-page-question-${activeSection}-${i}`}
                  >
                    <span className="flex-1 pr-4">{item.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 pt-1 border-t border-[#D4AF37]/15">
                    <div className="pt-4">{renderFaqAnswer(item.a, React)}</div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </AnimatePresence>

        {/* Contact helper */}
        <div className="mt-16 text-center border-t border-[#D4AF37]/15 pt-12">
          <p
            className="text-2xl sm:text-3xl text-[#1F3535] mb-3 font-montserrat tracking-tight font-medium"
          >
            Une autre <span className="font-playfair italic text-[#C46A4A] font-normal">question ?</span>
          </p>
          <p className="text-[#6B6B6B] text-sm font-body mb-5 max-w-lg mx-auto">
            Chaque projet est unique. Si un point mérite d'être clarifié pour votre cas, écrivez-moi, je vous réponds personnellement.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/#contact"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
                setTimeout(() => {
                  document.dispatchEvent(new CustomEvent("open-contact"));
                }, 300);
              }}
              className="inline-flex items-center gap-2 bg-[#C46A4A] text-white hover:bg-[#A6553A] px-6 py-3 rounded-full text-sm font-semibold font-body nav-text tracking-wide uppercase group transition-colors"
              data-testid="faq-page-contact-cta"
            >
              <Mail className="w-4 h-4" />
              Écrivez-moi directement
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/#services"
              className="inline-flex items-center gap-2 bg-white border border-[#BFBFBF] hover:border-[#D4AF37]/60 text-[#1F3535] px-6 py-3 rounded-full text-sm font-semibold font-body nav-text tracking-wide uppercase transition-colors"
              data-testid="faq-page-services-cta"
            >
              Voir les forfaits
            </Link>
          </div>
        </div>
      </div>

      {/* Minimal footer */}
      <footer className="border-t border-[#BFBFBF] py-8 bg-white">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#6B6B6B]/70 font-body">
            MonBeauSite — Émilien TERROT | Tous droits réservés
          </p>
          <div className="flex gap-5 text-xs font-body">
            <Link to="/cgv" className="text-[#6B6B6B]/70 hover:text-[#C46A4A] transition-colors">CGV</Link>
            <Link to="/mentions-legales" className="text-[#6B6B6B]/70 hover:text-[#C46A4A] transition-colors">Mentions légales</Link>
            <Link to="/blog" className="text-[#6B6B6B]/70 hover:text-[#C46A4A] transition-colors">Blog</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
